import { BookingContactEntry, BookingData } from "../../../types/bookings";
import { IContactFormData } from "../../../types/contact";
import { PRIMARY_CONTACT_ROLE } from "../constants/contact-roles";

// Shape a contact search result into the display object carried on a row.
export const buildRowContact = (value: IContactFormData | null) =>
  value
    ? {
        id: Number(value.id),
        first_name: value.first_name ?? null,
        last_name: value.last_name ?? null,
        name: value.name ?? null,
        email: value.email ?? null,
        phone: value.phone ?? null,
      }
    : null;

export const findPrimary = (contacts: BookingContactEntry[]) =>
  contacts.find((c) => c.role === PRIMARY_CONTACT_ROLE) || contacts[0] || null;

// Authoritative writer: persist the full contacts list and mirror the primary
// (role='primary', else the first row) into the legacy scalar fields so the
// existing Email/Phone inputs and the contact_id payload keep working.
export const withPrimaryMirrored = (
  prev: BookingData,
  nextContacts: BookingContactEntry[],
  extra: Partial<BookingData> = {},
): BookingData => {
  const primary = findPrimary(nextContacts);
  return {
    ...prev,
    ...extra,
    contacts: nextContacts,
    contact_id: primary ? Number(primary.contact_id) || null : null,
    first_name: primary?.contact?.first_name || "",
    surname: primary?.contact?.last_name || "",
    email: primary?.contact?.email || "",
    phone: primary?.contact?.phone || "",
  };
};

// Bookings created before multi-contact carry only a scalar contact_id. Seed a
// single primary row from it so loading one for edit is not destructive.
export const hydrateContacts = (
  contacts: BookingContactEntry[] | undefined | null,
  legacyContactId: number | null | undefined,
  legacyContact?: IContactFormData | null,
): BookingContactEntry[] => {
  if (contacts?.length) {
    return contacts.map((c) => ({
      contact_id: Number(c.contact_id),
      role: c.role,
      contact: c.contact ?? null,
    }));
  }
  if (legacyContactId) {
    return [
      {
        contact_id: Number(legacyContactId),
        role: PRIMARY_CONTACT_ROLE,
        contact: buildRowContact(legacyContact ?? null),
      },
    ];
  }
  return [];
};

// Full set of contacts (with roles) as a single JSON string. The backend parses
// `contacts`, mirrors the primary into contact_id, and rebuilds the
// BookingContact rows. Falls back to a single primary from contact_id.
// Only contact_id and role are sent; the `contact` display object is stripped.
export const appendContactsPayload = (
  formData: FormData,
  contacts: BookingContactEntry[] | undefined | null,
  effectiveContactId: number | null | undefined,
) => {
  const payload = contacts?.length
    ? contacts
        .filter((c) => c.contact_id)
        .map((c) => ({ contact_id: c.contact_id, role: c.role }))
    : effectiveContactId
      ? [{ contact_id: effectiveContactId, role: PRIMARY_CONTACT_ROLE }]
      : [];
  if (payload.length) {
    formData.append("contacts", JSON.stringify(payload));
  }
};
